import { useEffect, useState } from 'react';
import { Button, Skeleton } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { UrgentAction } from '@nxsys/types/api/urgentActions';
import {
  CategoryBar,
  CategoryLabel,
  CategoryPill,
  CountBadge,
  FooterRow,
  Header,
  HeaderSub,
  HeaderText,
  HeaderTitle,
  IconBox,
  ItemMain,
  ItemMeta,
  ItemRow,
  ItemTitle,
  ItemTopLine,
  RowAction,
  SnoozeText,
  StatusTag,
  StyledModal,
} from './index.styled';

const FPS_ROUTE = '/rti-filings/fps';

interface Props {
  open: boolean;
  actions: UrgentAction[];
  loading?: boolean;
  snoozing?: boolean;
  onClose: () => void;
  onSnooze: () => void;
}

const UrgentActionsPopup = ({
  open,
  actions,
  loading,
  snoozing,
  onClose,
  onSnooze,
}: Props) => {
  const navigate = useNavigateWithQuery();
  const [filter, setFilter] = useState<string | null>(null);

  // Reset the category filter each time the popup opens.
  useEffect(() => {
    if (open) setFilter(null);
  }, [open]);

  const sorted = [...actions].sort((a, b) => a.priority - b.priority);
  const rows = sorted.flatMap((a) =>
    a.items.map((item) => ({ ...item, category: a.category })),
  );
  const total = rows.length;
  const visibleRows = filter
    ? rows.filter((r) => r.category === filter)
    : rows;

  const categoryNames = sorted.map((a) => a.category);
  const subtitle =
    categoryNames.length <= 1
      ? `Submission issues across ${categoryNames[0] ?? 'FPS'}.`
      : `Submission issues across ${categoryNames
          .slice(0, -1)
          .join(', ')} and ${categoryNames[categoryNames.length - 1]}.`;

  const goTo = (ctaRoute: string) => {
    if (ctaRoute) navigate(ctaRoute);
    onClose();
  };

  return (
    <StyledModal
      open={open}
      onCancel={onClose}
      width={720}
      title={
        <Header>
          <CountBadge>{total}</CountBadge>
          <HeaderText>
            <HeaderTitle>Action required today</HeaderTitle>
            <HeaderSub>{subtitle}</HeaderSub>
          </HeaderText>
        </Header>
      }
      footer={
        <FooterRow>
          <SnoozeText onClick={onSnooze} disabled={snoozing}>
            Snooze all · 24h
          </SnoozeText>
          <Button
            type="primary"
            onClick={() => {
              navigate(FPS_ROUTE);
              onClose();
            }}
          >
            Submit now
          </Button>
        </FooterRow>
      }
    >
      {loading && total === 0 ? (
        <Skeleton active paragraph={{ rows: 4 }} style={{ padding: '8px 0' }} />
      ) : (
        <>
      <CategoryBar>
        {sorted.map((a) => (
          <CategoryPill
            key={a.category}
            $active={filter === a.category}
            onClick={() =>
              setFilter((prev) => (prev === a.category ? null : a.category))
            }
          >
            {`${a.category} · ${a.count}`}
          </CategoryPill>
        ))}
      </CategoryBar>

      {visibleRows.map((item) => (
        <ItemRow key={item.refId}>
          <IconBox>
            <FileTextOutlined />
          </IconBox>
          <ItemMain>
            <ItemTopLine>
              <CategoryLabel>{item.category}</CategoryLabel>
              <StatusTag>{item.statusLabel}</StatusTag>
            </ItemTopLine>
            <ItemTitle>{item.title}</ItemTitle>
            <ItemMeta>{`${item.companyName} · ${item.periodLabel} · ${
              item.payrollCount
            } payroll${item.payrollCount === 1 ? '' : 's'}`}</ItemMeta>
          </ItemMain>
          <RowAction>
            <Button onClick={() => goTo(item.ctaRoute)}>Resolve →</Button>
          </RowAction>
        </ItemRow>
      ))}
        </>
      )}
    </StyledModal>
  );
};

export default UrgentActionsPopup;
